import React, { useState, useEffect } from 'react';

interface LogEntry {
  id: number;
  fullId: string;
  username: string;
  date: string;
  wingoMined: number;
  kmLogged: number;
  initiation: boolean;
  category: string;
  activityLink: string;
}

const TestModal: React.FC = () => {
  const [showModal, setShowModal] = useState(true);
  const [gateUnlockCount, setGateUnlockCount] = useState(0);

  const handleClose = () => setShowModal(false);

  const fetchGateUnlockCount = async () => {
    try {
      const logRes = await fetch(`https://docs.google.com/spreadsheets/d/1eWkRJZph0X75PXfyWd22iKtgDsQhiv7RrH9X8xDKwu4/gviz/tq?tqx=out:csv&sheet=Log&t=${Date.now()}`, { cache: 'no-store' });
      const logText = await logRes.text();
      const logRows = logText.trim().split('\n');
      const data = logRows.slice(1); // skip header row
      
      const parsed = data
        .filter(row => row.trim() && row.split(',').length >= 13)
        .map((row) => {
          const columns = row.split(',');
          return {
            id: parseInt(columns[0]) || 0,
            fullId: columns[12]?.trim() || '',
            username: columns[1]?.trim() || '',
            date: columns[2]?.trim() || '',
            wingoMined: parseInt(columns[3]) || 0,
            kmLogged: parseFloat(columns[4]) || 0,
            initiation: columns[7]?.trim() === 'Yes',
            category: columns[10]?.trim() || '',
            activityLink: columns[5]?.trim() || ''
          };
        })
        .filter(entry => entry.username && entry.username !== '')
        .filter((entry, index, self) => 
          index === self.findIndex(e => e.id === entry.id)
        );

      // Count unique users with Gate Unlock transactions
      const gateUnlockUsers = new Set(
        parsed
          .filter(entry => entry.category === 'Gate Unlock')
          .map(entry => entry.username)
      );
      
      setGateUnlockCount(gateUnlockUsers.size);
    } catch (err) {
      console.error('Failed to fetch gate unlock count:', err);
    }
  };

  useEffect(() => {
    // Fetch gate unlock count
    fetchGateUnlockCount();
  }, []);

  return (
    <div className="test-modal-page min-h-screen flex items-center justify-center bg-gray-50">
      <button 
        className="mb-4 bg-wingo-600 hover:bg-wingo-700 text-white font-bold py-2 px-6 rounded-full shadow-lg focus:outline-none focus:ring-2 focus:ring-wingo-400 focus:ring-opacity-50 text-lg"
        onClick={() => setShowModal(true)}
      >
        Show WINGO World Welcome Modal
      </button>
      {showModal && (
        <div className="popup-container fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="popup-content bg-white rounded-xl shadow-2xl px-4 sm:px-8 pt-6 sm:pt-8 pb-4 max-w-md w-full mx-4 relative border-2 border-wingo-600/30 ring-2 ring-wingo-600/10">
            <button 
              className="popup-close absolute top-3 sm:top-4 right-3 sm:right-4 text-gray-400 hover:text-gray-700 text-3xl sm:text-2xl font-bold focus:outline-none p-2"
              onClick={handleClose}
              aria-label="Close"
            >
              ×
            </button>
            <div className="popup-header text-center mb-4">
              <div className="flex flex-col items-center justify-center gap-0">
                <span className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-wingo-600 flex items-center gap-2 mb-0">
                  WINGO World
                </span>
                <span className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#E6C200] leading-none">
                  10+1 <span className="text-gray-800">Program</span>
                </span>
              </div>
            </div>
            <div className="popup-body text-gray-700 space-y-4">
              <p className="text-lg sm:text-xl font-semibold text-center">10 <span className="text-[#E6C200] font-bold">W</span>INGO <span>(+1 Initiation Wingo)</span> unlocks:</p>
              <ul className="list-disc pl-6 space-y-1 text-base sm:text-lg">
                <li>The Old Balance Wingate Invitational</li>
                <li>Old Balance Early Drop Alerts</li>
                <li>The DAISY-Daniels DVOT Dashboard</li>
                <li>Custom <span className="font-bold">Coach D<span className="text-cyan-500 font-bold">AI</span>SY™</span> Wingo Workouts</li>
              </ul>
            </div>
            <div className="popup-cta mt-3 flex flex-col items-center justify-center gap-2">
              <a 
                href="sms:9299254744" 
                className="bg-gray-900 hover:bg-gray-800 text-white font-bold py-1.5 sm:py-1.5 px-8 sm:px-6 rounded-full shadow-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 text-lg sm:text-lg text-center no-underline w-full sm:w-auto"
                style={{ display: 'inline-block' }}
              >
                Text D<span className="text-cyan-500 font-bold">AI</span>SY™ to Enroll
              </a>
              <div className="text-center text-lg mt-1 mb-0" style={{ fontSize: '1.25rem' }}>
                🐎🤖🪽8️⃣
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Track stats container (copied from ledger) */}
      <div className="relative bg-white rounded-full shadow-sm border-[14px] border-orange-500 px-4 py-5 mb-6 max-w-[260px] mx-auto flex flex-col items-center justify-center overflow-hidden" style={{ minHeight: '120px', height: '120px', borderRadius: '9999px' }}>
        {/* Track lane markings - now closer to the border and thinner */}
        <div className="absolute inset-0 pointer-events-none z-10">
          {/* Top left */}
          <div className="absolute w-4 h-0.5 bg-white rounded-full left-2 top-3 rotate-[-25deg] opacity-90"></div>
          {/* Top right */}
          <div className="absolute w-4 h-0.5 bg-white rounded-full right-2 top-3 rotate-[25deg] opacity-90"></div>
          {/* Left middle */}
          <div className="absolute w-0.5 h-4 bg-white rounded-full left-0.5 top-1/2 -translate-y-1/2 rotate-[-10deg] opacity-90"></div>
          {/* Right middle */}
          <div className="absolute w-0.5 h-4 bg-white rounded-full right-0.5 top-1/2 -translate-y-1/2 rotate-[10deg] opacity-90"></div>
          {/* Bottom left */}
          <div className="absolute w-4 h-0.5 bg-white rounded-full left-2 bottom-3 rotate-[25deg] opacity-90"></div>
          {/* Bottom right */}
          <div className="absolute w-4 h-0.5 bg-white rounded-full right-2 bottom-3 rotate-[-25deg] opacity-90"></div>
        </div>
        <div className="flex flex-col w-full h-full items-center justify-center gap-0.5">
          <div className="flex flex-row items-center justify-center gap-2 w-full">
            <span className="text-gray-600 text-base">Total <span className="text-[#E6C200] font-bold">W</span> Mined:</span>
            <span className="font-bold text-base">585</span>
          </div>
          <div className="flex flex-row items-center justify-center gap-2 w-full">
            <span className="text-gray-600 text-base"><span className="text-[#E6C200] font-bold">W</span> in Circulation:</span>
            <span className="font-bold text-base">545</span>
          </div>
          <div className="flex flex-row items-center justify-center gap-2 w-full">
            <span className="text-gray-600 text-base">Wingo Footprint:</span>
            <span className="font-bold text-base">320 km</span>
          </div>
        </div>
      </div>
      <div className="text-center text-gray-400 mt-8">Test page for WINGO Track Stats Container</div>
      
      {/* Gate Unlock Count and RAQ Elements */}
      <div className="mt-8 space-y-4">
        <div className="bg-blue-50 py-2 px-4 rounded-lg border border-blue-100 inline-block">
          <p className="font-medium text-blue-700">
            <span className="font-bold">{gateUnlockCount} / 32 </span> Gates Unlocked
          </p>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg py-2 px-4 mt-4 ml-2 inline-block">
          <a href="/raq" className="text-sm font-medium text-gray-700 hover:text-wingo-600 transition-colors">
            RAQ
          </a>
        </div>
      </div>
      
      {/* TEMP: Avatar Preview for Joni and Coach_2 (Ledger Style, with custom crop and brightness) */}
      <div className="flex flex-col items-center gap-8 mt-8">
        {/* Joni: zoom in (dog closer) by shifting y up and increasing height, and reduce brightness */}
        <div className="flex flex-col items-center">
          <div className="flex-shrink-0" style={{ width: '2.5rem', height: '3rem' }}>
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 40 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ display: 'block' }}
            >
              <defs>
                <clipPath id="avatar-clip-preview-joni">
                  <path d="M 2 16 A 14 14 0 0 1 16 2 L 24 2 A 14 14 0 0 1 38 16 L 38 32 A 14 14 0 0 1 24 46 L 16 46 A 14 14 0 0 1 2 32 Z" />
                </clipPath>
                <linearGradient id="border-gradient-preview-joni" x1="0%" y1="100%" x2="0%" y2="0%">
                  <stop offset="0%" stopColor="#B02E0C" />
                  <stop offset="25%" stopColor="#FF7A00" />
                  <stop offset="50%" stopColor="#FFA733" />
                  <stop offset="75%" stopColor="#FF7A00" />
                  <stop offset="100%" stopColor="#B02E0C" />
                </linearGradient>
                <linearGradient id="shine-gradient-preview-joni" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(255, 167, 51, 0.9)" />
                  <stop offset="25%" stopColor="rgba(255, 167, 51, 0.6)" />
                  <stop offset="50%" stopColor="rgba(255, 167, 51, 0.3)" />
                  <stop offset="75%" stopColor="rgba(255, 167, 51, 0.6)" />
                  <stop offset="100%" stopColor="rgba(255, 167, 51, 0.9)" />
                </linearGradient>
                <filter id="joni-brightness" x="-20%" y="-20%" width="140%" height="140%">
                  <feComponentTransfer>
                    <feFuncR type="linear" slope="0.92" />
                    <feFuncG type="linear" slope="0.92" />
                    <feFuncB type="linear" slope="0.92" />
                  </feComponentTransfer>
                </filter>
              </defs>
              <path
                d="M 2 16 A 14 14 0 0 1 16 2 L 24 2 A 14 14 0 0 1 38 16 L 38 32 A 14 14 0 0 1 24 46 L 16 46 A 14 14 0 0 1 2 32 Z"
                stroke="url(#border-gradient-preview-joni)"
                strokeWidth="3"
                fill="white"
              />
              {/* Shine ON for Joni */}
              <path
                d="M 25 8 A 14 14 0 0 1 32 16"
                stroke="url(#shine-gradient-preview-joni)"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                opacity="0.9"
              />
              <path
                d="M 15 40 A 14 14 0 0 1 8 32"
                stroke="url(#shine-gradient-preview-joni)"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                opacity="0.8"
              />
              <image
                href="/avatars/joni.png"
                x="2"
                y="-12"
                width="36"
                height="60"
                clipPath="url(#avatar-clip-preview-joni)"
                preserveAspectRatio="xMidYMid slice"
                filter="url(#joni-brightness)"
              />
            </svg>
          </div>
          <span className="mt-2 font-semibold text-gray-700">Joni</span>
        </div>
        {/* Coach_2: fill the box better (less padding, more zoom, move down), NO SHINE */}
        <div className="flex flex-col items-center">
          <button className="flex-shrink-0" style={{ width: '2.5rem', height: '3rem' }} onClick={() => alert('Yellowstone Glow avatar clicked! (demo)')} aria-label="Open Yellowstone avatar">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 40 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ display: 'block' }}
            >
              <defs>
                <clipPath id="avatar-clip-preview-coach2">
                  <path d="M 2 16 A 14 14 0 0 1 16 2 L 24 2 A 14 14 0 0 1 38 16 L 38 32 A 14 14 0 0 1 24 46 L 16 46 A 14 14 0 0 1 2 32 Z" />
                </clipPath>
                <linearGradient id="border-gradient-preview-coach2" x1="0%" y1="100%" x2="0%" y2="0%">
                  <stop offset="0%" stopColor="#B02E0C" />
                  <stop offset="25%" stopColor="#FF7A00" />
                  <stop offset="50%" stopColor="#FFA733" />
                  <stop offset="75%" stopColor="#FF7A00" />
                  <stop offset="100%" stopColor="#B02E0C" />
                </linearGradient>
              </defs>
              <path
                d="M 2 16 A 14 14 0 0 1 16 2 L 24 2 A 14 14 0 0 1 38 16 L 38 32 A 14 14 0 0 1 24 46 L 16 46 A 14 14 0 0 1 2 32 Z"
                stroke="url(#border-gradient-preview-coach2)"
                strokeWidth="3"
                fill="white"
              />
              {/* Responsive y for desktop vs mobile */}
              <image
                href="/avatars/coach_2.png"
                x="2"
                y="5"
                width="36"
                height="52"
                clipPath="url(#avatar-clip-preview-coach2)"
                preserveAspectRatio="xMidYMid slice"
              />
            </svg>
          </button>
          <span className="mt-2 font-semibold text-gray-700">Coach_2</span>
        </div>
        {/* No Avatar: show SVG border with no image for reference */}
        <div className="flex flex-col items-center">
          <button className="flex-shrink-0" style={{ width: '2.5rem', height: '3rem' }} onClick={() => alert('Yellowstone Glow avatar clicked! (demo)')} aria-label="Open Yellowstone avatar">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 40 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ display: 'block' }}
            >
              <defs>
                <clipPath id="avatar-clip-preview-none">
                  <path d="M 2 16 A 14 14 0 0 1 16 2 L 24 2 A 14 14 0 0 1 38 16 L 38 32 A 14 14 0 0 1 24 46 L 16 46 A 14 14 0 0 1 2 32 Z" />
                </clipPath>
                <linearGradient id="border-gradient-preview-none" x1="0%" y1="100%" x2="0%" y2="0%">
                  <stop offset="0%" stopColor="#B02E0C" />
                  <stop offset="25%" stopColor="#FF7A00" />
                  <stop offset="50%" stopColor="#FFA733" />
                  <stop offset="75%" stopColor="#FF7A00" />
                  <stop offset="100%" stopColor="#B02E0C" />
                </linearGradient>
                <linearGradient id="shine-gradient-preview-none" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="rgba(255, 167, 51, 0.9)" />
                  <stop offset="25%" stopColor="rgba(255, 167, 51, 0.6)" />
                  <stop offset="50%" stopColor="rgba(255, 167, 51, 0.3)" />
                  <stop offset="75%" stopColor="rgba(255, 167, 51, 0.6)" />
                  <stop offset="100%" stopColor="rgba(255, 167, 51, 0.9)" />
                </linearGradient>
              </defs>
              <path
                d="M 2 16 A 14 14 0 0 1 16 2 L 24 2 A 14 14 0 0 1 38 16 L 38 32 A 14 14 0 0 1 24 46 L 16 46 A 14 14 0 0 1 2 32 Z"
                stroke="url(#border-gradient-preview-none)"
                strokeWidth="3"
                fill="white"
              />
              <path
                d="M 25 8 A 14 14 0 0 1 32 16"
                stroke="url(#shine-gradient-preview-none)"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                opacity="0.9"
              />
              <path
                d="M 15 40 A 14 14 0 0 1 8 32"
                stroke="url(#shine-gradient-preview-none)"
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                opacity="0.8"
              />
              {/* No image element here */}
            </svg>
          </button>
          <span className="mt-2 font-semibold text-gray-700">No Avatar</span>
        </div>
        {/* Yellowstone Glow Example: shiny glowy yellow border */}
        <div className="flex flex-col items-center">
          <button className="flex-shrink-0" style={{ width: '2.5rem', height: '3rem' }} onClick={() => alert('Yellowstone Glow avatar clicked! (demo)')} aria-label="Open Yellowstone avatar">
            <svg
              width="100%"
              height="100%"
              viewBox="0 0 40 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ display: 'block' }}
            >
              <defs>
                <clipPath id="avatar-clip-preview-yellow">
                  <path d="M 2 16 A 14 14 0 0 1 16 2 L 24 2 A 14 14 0 0 1 38 16 L 38 32 A 14 14 0 0 1 24 46 L 16 46 A 14 14 0 0 1 2 32 Z" />
                </clipPath>
                <linearGradient id="yellow-border-gradient-preview" x1="0%" y1="100%" x2="0%" y2="0%">
                  <stop offset="0%" stopColor="#B8860B" />
                  <stop offset="20%" stopColor="#DAA520" />
                  <stop offset="50%" stopColor="#FFD700" />
                  <stop offset="80%" stopColor="#DAA520" />
                  <stop offset="100%" stopColor="#B8860B" />
                </linearGradient>
                <filter id="yellow-glow-preview" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="2.5" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>
              {/* Pulsing glow underlay */}
              <path
                d="M 2 16 A 14 14 0 0 1 16 2 L 24 2 A 14 14 0 0 1 38 16 L 38 32 A 14 14 0 0 1 24 46 L 16 46 A 14 14 0 0 1 2 32 Z"
                stroke="#FFD700"
                strokeWidth="6"
                fill="none"
                filter="url(#yellow-glow-preview)"
                opacity="0.7"
              >
                <animate attributeName="opacity" values="0.9;0.5;0.9" dur="1.8s" repeatCount="indefinite" />
              </path>
              {/* Shiny gold ring */}
              <path
                d="M 2 16 A 14 14 0 0 1 16 2 L 24 2 A 14 14 0 0 1 38 16 L 38 32 A 14 14 0 0 1 24 46 L 16 46 A 14 14 0 0 1 2 32 Z"
                stroke="url(#yellow-border-gradient-preview)"
                strokeWidth="3"
                fill="white"
              />
              {/* Sample image (using default) */}
              <image
                href="/avatars/default.png"
                x="2"
                y="2"
                width="36"
                height="44"
                clipPath="url(#avatar-clip-preview-yellow)"
                preserveAspectRatio="xMidYMid slice"
              />
            </svg>
          </button>
          <span className="mt-2 font-semibold text-gray-700">Yellowstone Glow</span>
        </div>
      </div>
      <div className="text-center text-gray-400 mt-8">Test page for WINGO World Welcome 10+1 Modal</div>
    </div>
  );
};

export default TestModal; 